import { fetchPrivateApi } from '@/lib/api/apiServiceBase.ts';
import { InitUploadResponse } from '@/lib/upload/types.ts';
import { BankTransactionImportResultDto } from '@/features/dashboard/rawBankTransactions/types/BankTransactionImportResultDto.ts';
import { SupportedBankDto } from '@/features/dashboard/rawBankTransactions/types/SupportedBankDto.ts';
import { InitBankImportUploadRequest } from '@/features/dashboard/rawBankTransactions/types/InitBankImportUploadRequest.ts';
import { PagedDto } from '@/components/table/types/PagedDto.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { TableDefinitionDto } from '@/components/table/types/TableDefinitionDto.ts';
import { RawTransactionListDto } from '@/features/dashboard/rawBankTransactions/types/RawTransactionListDto.ts';
import { RawTransactionDto } from '@/features/dashboard/rawBankTransactions/types/RawTransactionDto.ts';
import { RawTransactionImportDto } from '@/features/dashboard/rawBankTransactions/types/RawTransactionImportDto.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import {
  DeleteRawTransactionsRequestDto,
  DeleteRawTransactionsResultDto,
  IgnoreTransactionRequestDto,
  MatchToBookingRequestDto,
  QualifyAsExpenseRequestDto,
  MatchToExpenseRequestDto,
} from '@/features/dashboard/rawBankTransactions/types/BankTransactionRequests.ts';
import { buildPageRequestParams } from '@/components/table/types/TableUtil.ts';

const rawBankTransactionsApi = {
  // --- Import ---
  getSupportedBanks: (): Promise<SupportedBankDto[]> =>
    fetchPrivateApi('GET', 'bank-transactions/import/supported-banks'),

  initUpload: (
    request: InitBankImportUploadRequest,
  ): Promise<InitUploadResponse> =>
    fetchPrivateApi('POST', 'bank-transactions/import/init-upload', request),

  processUpload: (fileId: string): Promise<BankTransactionImportResultDto> =>
    fetchPrivateApi('POST', `bank-transactions/import/${fileId}/process`),

  getImports: (): Promise<RawTransactionImportDto[]> =>
    fetchPrivateApi('GET', 'bank-transactions/imports'),

  getImport: (importId: string): Promise<RawTransactionImportDto> =>
    fetchPrivateApi('GET', `bank-transactions/imports/${importId}`),

  getImportTransactions: (importId: string): Promise<RawTransactionDto[]> =>
    fetchPrivateApi(
      'GET',
      `bank-transactions/imports/${importId}/transactions`,
    ),

  // --- Table (paginated list) ---
  getAll: (
    pageRequest: PageRequest,
    filterCriteria: FilterCriteriaDto,
  ): Promise<PagedDto<RawTransactionListDto>> => {
    const params = buildPageRequestParams(pageRequest);
    return fetchPrivateApi<PagedDto<RawTransactionListDto>>(
      'POST',
      `bank-transactions/all?${params.toString()}`,
      filterCriteria,
    );
  },

  getTableDefinition: (): Promise<TableDefinitionDto> =>
    fetchPrivateApi('GET', 'bank-transactions/table-definition'),

  // --- Single transaction ---
  getTransaction: (id: string): Promise<RawTransactionDto> =>
    fetchPrivateApi('GET', `bank-transactions/${id}`),

  // --- Qualification actions ---
  matchToBooking: (
    id: string,
    request: MatchToBookingRequestDto,
  ): Promise<RawTransactionDto> =>
    fetchPrivateApi(
      'POST',
      `bank-transactions/${id}/match-to-booking`,
      request,
    ),

  /** Tworzy nowy wydatek z przelewu i od razu wpina w niego zapłatę. */
  qualifyAsExpense: (
    id: string,
    request: QualifyAsExpenseRequestDto,
  ): Promise<RawTransactionDto> =>
    fetchPrivateApi(
      'POST',
      `bank-transactions/${id}/qualify-as-expense`,
      request,
    ),

  /** Wpina przelew w istniejący, już zaplanowany wydatek. */
  matchToExpense: (
    id: string,
    request: MatchToExpenseRequestDto,
  ): Promise<RawTransactionDto> =>
    fetchPrivateApi(
      'POST',
      `bank-transactions/${id}/match-to-expense`,
      request,
    ),

  ignore: (
    id: string,
    request?: IgnoreTransactionRequestDto,
  ): Promise<RawTransactionDto> =>
    fetchPrivateApi('POST', `bank-transactions/${id}/ignore`, request),

  revert: (id: string): Promise<RawTransactionDto> =>
    fetchPrivateApi('POST', `bank-transactions/${id}/revert`),

  // --- Kasowanie wierszy wyciągu ---
  /** Odpowiedź to 204 bez ciała. */
  delete: (id: string): Promise<void> =>
    fetchPrivateApi('DELETE', `bank-transactions/${id}`),

  /**
   * Kasowanie paczki idzie `POST .../delete`, bo `DELETE` z ciałem bywa gubione
   * po drodze. Wszystko albo nic: jeden wpięty przelew odrzuca całą paczkę.
   */
  deleteMany: (ids: string[]): Promise<DeleteRawTransactionsResultDto> => {
    const request: DeleteRawTransactionsRequestDto = { ids };
    return fetchPrivateApi('POST', 'bank-transactions/delete', request);
  },
};

export default rawBankTransactionsApi;
